import * as React from 'react';
import { BarChart3 } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, Surface } from './Card';
import { ChartLegend } from './ChartLegend';
import { Inline, Stack } from './Layout';
import { Loading } from './Spinner';
import { Text } from './Text';

export interface DailyCallsChartDay {
  /** The axis label — "Mon", "Tue". */
  day: string;
  count: number;
  /** The full date for the tooltip; the day label stands in when absent. */
  date?: string;
}

export interface DailyCallsChartViewProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  data: DailyCallsChartDay[];
  loading?: boolean;
  title?: React.ReactNode;
}

interface TipProps {
  active?: boolean;
  label?: string;
  payload?: { value: number; payload: DailyCallsChartDay }[];
}

/* Recharts clones this with the hovered bar's data; it draws nothing itself. */
function DailyCallsTip({ active, payload, label }: TipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0];
  return (
    <Surface bordered radius="lg" padding="sm" elevation="overlay">
      <Text variant="body-sm-strong">{point.payload.date || label}</Text>
      <Text variant="body-sm" tone="muted">
        <Text as="span" variant="body-sm-strong" tone="default">
          {point.value}
        </Text>{' '}
        calls completed
      </Text>
    </Surface>
  );
}

const LEGEND = [
  { label: 'Completed calls', series: 1 },
  { label: 'Busiest day', series: 2 },
] as const;

/**
 * A week of completed calls as bars, with the busiest day picked out.
 *
 *   <DailyCallsChartView data={[{ day: 'Mon', count: 4 }, …]} />
 *
 * Takes the seven days already shaped — the caller fills in empty days. The
 * total in the header is the sum of what it is given.
 */
export const DailyCallsChartView = React.forwardRef<
  HTMLDivElement,
  DailyCallsChartViewProps
>(function DailyCallsChartView(
  { data, loading = false, title = 'Daily Calls (Last 7 Days)', ...props },
  ref
) {
  const max = Math.max(...data.map((d) => d.count), 0);
  const total = data.reduce((sum, d) => sum + d.count, 0);

  if (loading) {
    return (
      <Card ref={ref} {...props}>
        <CardHeader>
          <Inline gap={2} align="center">
            <BarChart3 className="size-icon-md text-chart-2" />
            <CardTitle>{title}</CardTitle>
          </Inline>
        </CardHeader>
        <CardContent className="h-[250px]">
          <Loading fill="parent" pad="none" title="Loading chart data..." />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card ref={ref} {...props}>
      <CardHeader>
        <Inline gap={0} align="center" justify="between">
          <Inline gap={2} align="center">
            <BarChart3 className="size-icon-md text-primary" />
            <CardTitle>{title}</CardTitle>
          </Inline>
          <Text as="span" variant="body-sm" tone="muted">
            Total:{' '}
            <Text as="span" variant="body-sm-strong" tone="default">
              {total}
            </Text>
          </Text>
        </Inline>
      </CardHeader>
      <CardContent>
        <Stack gap={2}>
          <Stack gap={0} className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="hsl(var(--border))"
                  opacity={0.5}
                />
                <XAxis
                  dataKey="day"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                  allowDecimals={false}
                  width={30}
                />
                <Tooltip
                  content={<DailyCallsTip />}
                  cursor={{ fill: 'hsl(var(--muted))', opacity: 0.3 }}
                />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={50}>
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        entry.count === max && max > 0
                          ? 'hsl(var(--chart-2))'
                          : 'hsl(var(--chart-1))'
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Stack>
          <ChartLegend items={[...LEGEND]} />
        </Stack>
      </CardContent>
    </Card>
  );
});
